import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BRAND, waLink, GABRIEL_AVATAR } from "@/lib/constants";
import { BrandedImage } from "@/components/site/BrandedImage";
import { FeaturedPetition } from "@/components/site/FeaturedPetition";
import { LeaderboardJudete } from "@/components/site/LeaderboardJudete";
import { WallOfChampions } from "@/components/site/WallOfChampions";
import {
  Shield, Users, HandHeart, Megaphone, Headphones, MessageCircle, Check,
  ArrowRight, ChevronRight, Play, Heart, FileText, Edit3, LifeBuoy, ShieldAlert,
  BookOpen, Building2, Mail, Facebook, Instagram, Youtube, Music2, Calendar,
  Radar as RadarIcon, Compass, Activity, Sparkles, AlertTriangle
} from "lucide-react";

const InfoBullet = ({ icon: Icon, title, text }) => (
  <div className="flex items-start gap-3">
    <div className="shrink-0 w-9 h-9 rounded-md bg-[#D4A63D]/10 border border-[#D4A63D]/30 grid place-items-center">
      <Icon className="w-4.5 h-4.5 text-[#D4A63D]" />
    </div>
    <div>
      <div className="text-[11px] font-bold tracking-[0.18em] text-[#D4A63D] uppercase">{title}</div>
      <div className="text-[12px] text-[#A0AABF] leading-snug mt-0.5">{text}</div>
    </div>
  </div>
);

const Stat = ({ icon: Icon, num, label }) => (
  <div className="flex items-center gap-3 p-3">
    <Icon className="w-7 h-7 text-[#D4A63D] shrink-0" />
    <div>
      <div className="font-display text-xl md:text-2xl text-[#D4A63D] leading-none">{num}</div>
      <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-[#A0AABF] leading-tight mt-0.5">{label}</div>
    </div>
  </div>
);

const ActionCTA = ({ icon: Icon, title, text, button, to, variant = "gold" }) => (
  <div className="dense-card p-5 flex flex-col">
    <Icon className="w-9 h-9 text-[#D4A63D] mb-3" />
    <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#D4A63D] mb-1.5">{title}</div>
    <p className="text-[12.5px] text-[#A0AABF] leading-snug mb-4 flex-1">{text}</p>
    <Link to={to} className={`${variant === "gold" ? "btn-gold" : "btn-gold-outline"} rounded-sm px-4 py-2.5 text-[11px] font-bold tracking-wider text-center`}>
      {button}
    </Link>
  </div>
);

export default function Home() {
  const [stats, setStats] = useState({ parents_helped: 19847, articles: 1200, videos: 2400, signatures: 12300, comments: 46000, institutions: 3100 });
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [stories, setStories] = useState([]);
  const [petitions, setPetitions] = useState([]);

  useEffect(() => {
    api.get("/stats").then(r => {
      setStats(s => ({
        parents_helped: Math.max(r.data.parents_helped, 19847),
        articles: 1200, videos: 2400, comments: 46000,
        signatures: Math.max(r.data.signatures, 12300),
        institutions: 3100,
      }));
    }).catch(() => {});
    api.get("/articles", { params: { limit: 4 } }).then(r => setArticles(r.data || [])).catch(() => {});
    api.get("/videos", { params: { limit: 4 } }).then(r => setVideos(r.data || [])).catch(() => {});
    api.get("/stories", { params: { limit: 4 } }).then(r => setStories(r.data || [])).catch(() => {});
    api.get("/petitions").then(r => setPetitions((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  const featured = articles[0];
  const featuredVid = videos[0];
  const featuredStory = stories[0];

  return (
    <div data-testid="page-home" className="bg-[#0A1020]">
      {/* TOP DENSE LAYOUT (HERO + INFO COL + SIDEBAR) */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-10 pb-8">
        <div className="grid grid-cols-12 gap-5">
          {/* LEFT: Hero text */}
          <div className="col-span-12 lg:col-span-5 fade-up">
            <h1 className="font-display text-[36px] sm:text-[44px] lg:text-[54px] xl:text-[60px] leading-[1.02] text-[#F8F8F4]">
              Niciun copil
              <span className="block text-[#F8F8F4]/95 mt-1">nu ar trebui să fie rupt</span>
              <span className="block gold-text mt-1">de un părinte care îl iubește.</span>
            </h1>
            <p className="mt-5 text-[15px] text-[#A0AABF] leading-relaxed max-w-md">
              Platforma <span className="text-[#F8F8F4]">Tati și Sofia</span> este despre conștientizare, educație, sprijin și acțiune pentru părinții și copiii afectați de <span className="rose-text font-medium">alienarea parentală</span> și conflictele din sistemul de protecție.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/ajutor" data-testid="hero-cta-help" className="btn-gold rounded-sm px-6 py-3.5 text-[12px] font-bold tracking-wider inline-flex items-center gap-2">
                <Heart className="w-4 h-4" /> AI NEVOIE DE AJUTOR?
              </Link>
              <Link to="/spune-povestea" data-testid="hero-cta-story" className="btn-gold-outline rounded-sm px-6 py-3.5 text-[12px] font-bold tracking-wider inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> SPUNE POVESTEA TA
              </Link>
              <Link
                to="/doneaza"
                data-testid="hero-cta-donate"
                className="rounded-sm px-6 py-3.5 text-[12px] font-bold tracking-wider inline-flex items-center gap-2 border border-[#E89BB8]/60 text-[#E89BB8] hover:bg-[#E89BB8]/10 hover:border-[#E89BB8] transition"
              >
                <Heart className="w-4 h-4 fill-[#E89BB8]" /> SUSȚINE ACEASTĂ MISIUNE
              </Link>
            </div>
          </div>

          {/* CENTER: Hero image (cinematic father+daughter, no text overlay) */}
          <div className="col-span-12 lg:col-span-4 fade-up-delay-1">
            <div className="relative aspect-[4/5] lg:aspect-[3/4] rounded-xl overflow-hidden border border-white/10 shadow-2xl">
              <BrandedImage
                src={BRAND.heroDreams}
                alt="Tată și fiică privind împreună spre vise"
                className="absolute inset-0 w-full h-full hero-scale"
                watermarkPosition="br"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1020]/85 via-[#0A1020]/20 to-transparent pointer-events-none z-[5]" />
            </div>
          </div>

          {/* RIGHT: 4 info bullets + sidebar Gabriel */}
          <div className="col-span-12 lg:col-span-3 fade-up-delay-2 space-y-4">
            <div className="dense-card p-5 space-y-4">
              <InfoBullet icon={Shield} title="INFORMEAZĂ-TE" text="Cunoașterea este primul pas spre schimbare." />
              <InfoBullet icon={Users} title="AI DREPTURI" text="Legea îți oferă dreptul la copilul tău." />
              <InfoBullet icon={HandHeart} title="NU EȘTI SINGUR" text="Mii de părinți trec prin aceeași luptă." />
              <InfoBullet icon={Megaphone} title="ACȚIONEAZĂ" text="Împreună putem schimba realitatea." />
            </div>

            {/* GABRIEL CARD */}
            <div className="dense-card p-5 text-center">
              <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#A0AABF] mb-2">Ai nevoie de ajutor?</div>
              <div className="font-display text-lg text-[#F8F8F4] mb-1">Vorbește cu Gabriel</div>
              <p className="text-[11px] text-[#A0AABF] mb-3">O discuție poate face diferența.</p>
              <ul className="text-left text-[11px] text-[#F8F8F4]/85 space-y-1 mb-4 bg-[#0A1020]/60 p-3 rounded-sm border border-white/5">
                <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#3BA76D]" /> Confidențial</li>
                <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#3BA76D]" /> Fără judecată</li>
                <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#3BA76D]" /> Ghidare și orientare</li>
              </ul>
              <Link to="/programare" data-testid="sidebar-cta-booking" className="block btn-gold rounded-sm py-3 text-[11px] font-bold tracking-wider mb-2">
                <Headphones className="w-3.5 h-3.5 inline mr-1.5" /> PROGRAMEAZĂ O DISCUȚIE
              </Link>
              <a href={waLink()} target="_blank" rel="noreferrer" className="block btn-gold-outline rounded-sm py-3 text-[11px] font-bold tracking-wider" data-testid="sidebar-cta-whatsapp">
                <MessageCircle className="w-3.5 h-3.5 inline mr-1.5" /> SCRIE PE WHATSAPP
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SCAN AP — SCANER DE ALIENARE PARENTALĂ (top promo, cinematic) */}
      <section
        data-testid="home-scanap-promo"
        className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mb-10 fade-up"
      >
        <div className="relative overflow-hidden rounded-xl border border-[#D4A63D]/25 bg-gradient-to-br from-[#0F1830] via-[#12193A] to-[#0A1020]">
          {/* Ambient glow accents */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#D4A63D]/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#E89BB8]/10 blur-3xl" />

          <div className="relative grid lg:grid-cols-[1.35fr_1fr] gap-8 lg:gap-10 p-6 md:p-10">
            {/* LEFT — description */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4A63D]/40 bg-[#D4A63D]/10 text-[#D4A63D] text-[10px] tracking-[0.25em] uppercase">
                <Sparkles className="w-3 h-3" /> Nou · Instrument Premium
              </div>
              <h2 className="mt-4 font-display text-3xl md:text-5xl leading-[1.05] text-white">
                Scanerul de <span className="gold-text">Alienare Parentală</span>
              </h2>
              <p className="mt-3 text-[15px] text-[#A0AABF] leading-relaxed max-w-2xl">
                <b className="text-white">SCAN AP</b> este un evaluator comportamental care te ajută să vezi <span className="text-white">harta observațiilor tale</span> pe 5 dimensiuni. Nu este diagnostic. Nu identifică vinovați. Prioritizează întotdeauna siguranța copilului.
              </p>

              {/* 5 dimensions bar */}
              <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-2.5">
                {[
                  { l: "Influențare", c: "#D4A63D", i: Compass },
                  { l: "Siguranță", c: "#C83A3A", i: Shield },
                  { l: "Preferință", c: "#5FA0E8", i: Heart },
                  { l: "Atașament", c: "#9C8FE0", i: HandHeart },
                  { l: "Conflict", c: "#6C7A9C", i: Activity },
                ].map((d) => {
                  const Icon = d.i;
                  return (
                    <div
                      key={d.l}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-md border border-white/10 bg-white/[0.03]"
                      style={{ borderColor: `${d.c}30` }}
                    >
                      <Icon className="w-4 h-4 shrink-0" style={{ color: d.c }} />
                      <span className="text-[11px] font-semibold tracking-wide text-[#F8F8F4]/90">{d.l}</span>
                    </div>
                  );
                })}
              </div>

              {/* Features chips */}
              <div className="mt-5 flex flex-wrap gap-2 text-[11px] text-[#A0AABF]">
                <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10">39 întrebări</span>
                <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10">Radar interactiv</span>
                <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10">Rezumat AI empatic</span>
                <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10">Raport imprimabil</span>
                <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10">Anonim</span>
                <span className="px-2.5 py-1 rounded-full bg-[#3BA76D]/10 border border-[#3BA76D]/30 text-[#3BA76D]">Gratuit</span>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to="/scan-ap"
                  data-testid="home-scanap-cta"
                  className="btn-gold rounded-sm px-7 py-3.5 text-[12px] font-bold tracking-widest inline-flex items-center gap-2"
                >
                  <RadarIcon className="w-4 h-4" /> ÎNCEPE EVALUAREA
                </Link>
                <Link
                  to="/scan-ap"
                  data-testid="home-scanap-learn"
                  className="text-[#A0AABF] hover:text-[#D4A63D] text-[12px] font-semibold tracking-wider inline-flex items-center gap-1.5"
                >
                  Cum funcționează <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <div className="mt-6 flex items-start gap-2 text-[11px] text-[#6C7A9C] leading-relaxed">
                <AlertTriangle className="w-3.5 h-3.5 text-[#C83A3A] shrink-0 mt-0.5" />
                <span>Dacă există pericol imediat: <b className="text-white">112</b> · <b className="text-white">119</b> (linie națională copii) · <b className="text-white">0800 801 200</b>.</span>
              </div>
            </div>

            {/* RIGHT — visual radar-like cadran */}
            <div className="relative">
              <div className="aspect-square max-w-[380px] mx-auto relative">
                {/* Concentric rings */}
                <div className="absolute inset-0 rounded-full border border-[#D4A63D]/15" />
                <div className="absolute inset-6 rounded-full border border-[#D4A63D]/20" />
                <div className="absolute inset-14 rounded-full border border-[#D4A63D]/25" />
                <div className="absolute inset-24 rounded-full border border-[#D4A63D]/30" />
                <div className="absolute inset-[38%] rounded-full bg-gradient-to-br from-[#D4A63D]/25 to-[#E89BB8]/20 border border-[#D4A63D]/40 backdrop-blur-sm grid place-items-center">
                  <RadarIcon className="w-8 h-8 text-[#D4A63D]" />
                </div>
                {/* Cross lines */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-full h-px bg-[#D4A63D]/10" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-full w-px bg-[#D4A63D]/10" />
                </div>
                {/* Dimension labels around */}
                {[
                  { l: "INFLUENȚARE", top: "4%", left: "50%", tx: "-50%", ty: "0", c: "#D4A63D" },
                  { l: "SIGURANȚĂ", top: "50%", left: "96%", tx: "-100%", ty: "-50%", c: "#C83A3A" },
                  { l: "PREFERINȚĂ", top: "78%", left: "88%", tx: "-100%", ty: "0", c: "#5FA0E8" },
                  { l: "ATAȘAMENT", top: "78%", left: "12%", tx: "0", ty: "0", c: "#9C8FE0" },
                  { l: "CONFLICT", top: "50%", left: "4%", tx: "0", ty: "-50%", c: "#6C7A9C" },
                ].map((p) => (
                  <div
                    key={p.l}
                    className="absolute text-[9px] font-bold tracking-[0.2em] uppercase"
                    style={{ top: p.top, left: p.left, transform: `translate(${p.tx}, ${p.ty})`, color: p.c }}
                  >
                    {p.l}
                  </div>
                ))}
                {/* Polygon sample */}
                <svg viewBox="0 0 200 200" className="absolute inset-8">
                  <polygon
                    points="100,25 165,80 145,155 55,155 35,80"
                    fill="url(#scanGrad)"
                    stroke="#D4A63D"
                    strokeWidth="1.5"
                    strokeOpacity="0.7"
                  />
                  <defs>
                    <linearGradient id="scanGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#D4A63D" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#E89BB8" stopOpacity="0.25" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div className="mt-4 text-center text-[10px] tracking-[0.25em] uppercase text-[#6C7A9C]">
                Exemplu · Radar 5 dimensiuni
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PETITION: 1-click sign + live progress + share buttons */}
      <FeaturedPetition />

      {/* TOP JUDEȚE LEADERBOARD */}
      <LeaderboardJudete />

      {/* WALL OF CHAMPIONS: top reputation contributors with quotes */}
      <WallOfChampions />

      {/* STATS BENTO BAR */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="dense-card grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-white/5">
          <Stat icon={Users} num={`${stats.parents_helped.toLocaleString("ro-RO")}+`} label="Părinți în comunitate" />
          <Stat icon={FileText} num="1.200+" label="Articole și ghiduri" />
          <Stat icon={Play} num="2.400+" label="Videouri educaționale" />
          <Stat icon={MessageCircle} num="46.000+" label="Comentarii și discuții" />
          <Stat icon={Heart} num={`${stats.signatures.toLocaleString("ro-RO")}+`} label="Semnături petiții" />
          <Stat icon={Building2} num="3.100+" label="Instituții în România" />
        </div>
      </section>

      {/* SISTER PROJECT — RESTART */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <a
          href="https://restart.tatisisofia.ro/"
          target="_blank"
          rel="noreferrer"
          data-testid="home-restart-cta"
          className="group block relative overflow-hidden rounded-md border border-[#E89BB8]/30 bg-gradient-to-r from-[#E89BB8]/10 via-[#0F1830] to-[#0A1020] p-6 md:p-7"
        >
          <div className="pointer-events-none absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[#E89BB8]/15 blur-3xl" />
          <div className="relative grid md:grid-cols-[1fr_auto] items-center gap-5">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#E89BB8]/40 bg-[#E89BB8]/10 text-[#E89BB8] text-[10px] tracking-[0.25em] uppercase font-bold mb-3">
                Proiect frate · restart.tatisisofia.ro
              </div>
              <h2 className="font-display text-2xl md:text-3xl text-white leading-tight">
                Programele <span className="text-[#E89BB8]">RESTART</span> — reconstrucție reală după divorț, alienare sau relații toxice
              </h2>
              <p className="mt-2 text-[#A0AABF] text-sm md:text-base leading-relaxed max-w-3xl">
                Rutină, alimentație, mișcare, somn, mindset. Un program construit pentru părinții care vor să se ridice, nu doar să supraviețuiască. <span className="text-white">Există și oportunitatea de a deveni manager județean</span> sau reprezentant local al rețelei Tati & Sofia.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-2 shrink-0">
              <span className="px-5 py-3 rounded-sm text-xs font-bold tracking-widest bg-[#E89BB8] text-[#0A1020] group-hover:brightness-110 transition inline-flex items-center gap-2 justify-center">
                DESCOPERĂ RESTART →
              </span>
              <span className="px-5 py-3 rounded-sm text-xs font-bold tracking-widest border border-[#E89BB8]/40 text-[#E89BB8] inline-flex items-center gap-2 justify-center">
                MANAGER JUDEȚEAN
              </span>
            </div>
          </div>
        </a>
      </section>

      {/* 3-COLUMN DENSE CONTENT + SIDEBAR PETITIONS */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="grid grid-cols-12 gap-5">
          {/* LEFT 9 cols: 3 mini-blocks */}
          <div className="col-span-12 lg:col-span-9 grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* ARTICLES */}
            <div className="dense-card overflow-hidden flex flex-col">
              <div className="px-4 py-3 flex items-center justify-between border-b border-white/5">
                <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#A0AABF]">Articol recomandat</div>
                <Link to="/articole" className="text-[10px] font-bold tracking-widest text-[#E89BB8] hover:text-[#F2BBD0] flex items-center gap-1">VEZI TOATE <ArrowRight className="w-3 h-3" /></Link>
              </div>
              {featured && (
                <Link to={`/articole/${featured.slug}`} data-testid="home-featured-article" className="group">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <BrandedImage
                      src={featured.hero_image}
                      alt={featured.title}
                      className="absolute inset-0 w-full h-full group-hover:scale-105 transition-transform duration-700"
                      watermarkPosition="br"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1020] via-transparent to-transparent pointer-events-none z-[5]" />
                    <span className="absolute top-2.5 left-2.5 tag-rose text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm z-10">{featured.category}</span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-display text-base text-[#F8F8F4] leading-snug group-hover:text-[#D4A63D] line-clamp-2">{featured.title}</h3>
                    <p className="text-[12px] text-[#A0AABF] mt-2 line-clamp-2 leading-snug">{featured.excerpt}</p>
                    <div className="mt-3 text-[10px] text-[#6C7A9C] uppercase tracking-widest">{featured.reading_time} min citire</div>
                  </div>
                </Link>
              )}
              <div className="px-4 pb-4 space-y-2 border-t border-white/5 pt-3">
                {articles.slice(1, 3).map(a => (
                  <Link key={a.article_id} to={`/articole/${a.slug}`} className="flex gap-2 group">
                    <span className="tag-rose text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm h-fit mt-0.5">{a.category}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-[#F8F8F4] group-hover:text-[#D4A63D] line-clamp-1 font-medium">{a.title}</div>
                      <div className="text-[10px] text-[#6C7A9C]">{a.reading_time} min</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* VIDEOS */}
            <div className="dense-card overflow-hidden flex flex-col">
              <div className="px-4 py-3 flex items-center justify-between border-b border-white/5">
                <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#A0AABF]">Video nou</div>
                <Link to="/video" className="text-[10px] font-bold tracking-widest text-[#E89BB8] hover:text-[#F2BBD0] flex items-center gap-1">VEZI TOATE <ArrowRight className="w-3 h-3" /></Link>
              </div>
              {featuredVid && (
                <Link to={`/video/${featuredVid.video_id}`} data-testid="home-featured-video" className="group">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <BrandedImage
                      src={featuredVid.thumbnail}
                      alt={featuredVid.title}
                      className="absolute inset-0 w-full h-full group-hover:scale-105 transition-transform duration-700"
                      watermarkPosition="tr"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1020]/90 via-transparent to-transparent pointer-events-none z-[5]" />
                    <div className="absolute inset-0 grid place-items-center z-10"><div className="w-14 h-14 rounded-full bg-[#D4A63D] grid place-items-center shadow-2xl"><Play className="w-6 h-6 fill-current text-[#0A1020]" /></div></div>
                    <div className="absolute bottom-2 right-2 text-[10px] bg-[#0A1020]/90 px-1.5 py-0.5 rounded-sm font-mono z-10">{Math.floor(featuredVid.duration/60)}:{String(featuredVid.duration%60).padStart(2,"0")}</div>
                    <div className="absolute bottom-3 left-3 right-3 z-10">
                      <h3 className="font-display text-sm text-[#F8F8F4] leading-tight line-clamp-2">{featuredVid.title}</h3>
                    </div>
                  </div>
                </Link>
              )}
              <div className="px-4 pb-4 space-y-2.5 border-t border-white/5 pt-3 flex-1">
                {videos.slice(1, 4).map(v => (
                  <Link key={v.video_id} to={`/video/${v.video_id}`} className="flex gap-2 group">
                    <div className="w-14 h-9 rounded-sm overflow-hidden shrink-0 relative">
                      <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" />
                      <Play className="absolute inset-0 m-auto w-3 h-3 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-[#F8F8F4] group-hover:text-[#D4A63D] line-clamp-2 leading-tight">{v.title}</div>
                      <div className="text-[10px] text-[#6C7A9C] font-mono">{Math.floor(v.duration/60)}:{String(v.duration%60).padStart(2,"0")}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* STORIES */}
            <div className="dense-card overflow-hidden flex flex-col">
              <div className="px-4 py-3 flex items-center justify-between border-b border-white/5">
                <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#A0AABF]">Povești care inspiră</div>
                <Link to="/comunitate" className="text-[10px] font-bold tracking-widest text-[#E89BB8] hover:text-[#F2BBD0] flex items-center gap-1">VEZI TOATE <ArrowRight className="w-3 h-3" /></Link>
              </div>
              {featuredStory && (
                <Link to={`/comunitate/${featuredStory.story_id}`} data-testid="home-featured-story" className="group">
                  <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#1A2340] to-[#0A1020]">
                    <BrandedImage
                      src={BRAND.imgChurchPath}
                      alt={featuredStory.title}
                      className="absolute inset-0 w-full h-full opacity-70 group-hover:opacity-90 transition-opacity duration-700"
                      watermarkPosition="tr"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1020] via-[#0A1020]/30 to-transparent pointer-events-none z-[5]" />
                    <div className="absolute inset-x-4 bottom-3">
                      <p className="font-quote text-base text-[#F8F8F4] italic leading-snug line-clamp-3">„{featuredStory.title}”</p>
                      <div className="text-[10px] text-[#E89BB8] mt-2 uppercase tracking-widest flex items-center gap-2">
                        <span>{featuredStory.county}</span>
                        <Heart className="w-3 h-3 fill-current" />
                        <span>{featuredStory.reactions_count}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              )}
              <div className="px-4 pb-4 space-y-2.5 border-t border-white/5 pt-3 flex-1">
                {stories.slice(1, 4).map(s => (
                  <Link key={s.story_id} to={`/comunitate/${s.story_id}`} className="block group">
                    <div className="font-quote text-[13px] italic text-[#F8F8F4] group-hover:text-[#D4A63D] line-clamp-2 leading-snug">„{s.title}”</div>
                    <div className="text-[10px] text-[#6C7A9C] mt-0.5 flex items-center gap-2">
                      <span className="uppercase tracking-widest">{s.county}</span>
                      <Heart className="w-2.5 h-2.5 text-[#E89BB8] fill-current" />
                      <span>{s.reactions_count}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT 3 cols: Petitions + Live community */}
          <div className="col-span-12 lg:col-span-3 space-y-5">
            <div className="dense-card overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between border-b border-white/5">
                <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#A0AABF]">Petiții active</div>
                <Link to="/petitii" className="text-[10px] font-bold tracking-widest text-[#E89BB8] hover:text-[#F2BBD0] flex items-center gap-1">VEZI TOATE <ArrowRight className="w-3 h-3" /></Link>
              </div>
              <div className="p-4 space-y-4">
                {petitions.map(p => {
                  const pct = Math.min(100, (p.current_signatures / p.goal_signatures) * 100);
                  return (
                    <Link key={p.petition_id} to={`/petitii/${p.slug}`} data-testid={`sidebar-pet-${p.slug}`} className="block group">
                      <div className="text-[12.5px] text-[#F8F8F4] group-hover:text-[#D4A63D] leading-snug font-medium line-clamp-3">{p.title}</div>
                      <div className="text-[10px] text-[#A0AABF] mt-1.5">{p.current_signatures.toLocaleString("ro-RO")} / {p.goal_signatures.toLocaleString("ro-RO")} semnături</div>
                      <div className="h-1 bg-[#0A1020] rounded-full overflow-hidden mt-1.5">
                        <div className="h-full progress-rose" style={{ width: `${pct}%` }} />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="dense-card p-4">
              <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#A0AABF] mb-3">În comunitate acum</div>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-7 h-7 rounded-full bg-gradient-to-br from-[#D4A63D] to-[#E89BB8] border-2 border-[#131B2E] grid place-items-center text-[10px] text-[#0A1020] font-bold">{["A","M","R","S"][i-1]}</div>
                  ))}
                </div>
                <div className="text-[12px] text-[#F8F8F4]">
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#3BA76D] animate-pulse" />
                    <strong className="text-[#D4A63D]">1.248</strong> membri online
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-[#A0AABF]">Comunitate sigură. Spațiu de înțelegere și sprijin.</p>
            </div>

            <div className="dense-card p-4">
              <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#A0AABF] mb-3 flex items-center justify-between">
                <span>Următoarele evenimente</span>
                <Link to="/comunitate" className="text-[#E89BB8] hover:text-[#F2BBD0]">VEZI</Link>
              </div>
              <div className="flex gap-3">
                <div className="shrink-0 w-12 text-center">
                  <div className="font-display text-2xl text-[#D4A63D] leading-none">25</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#A0AABF] mt-0.5">Mai</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] text-[#F8F8F4] leading-snug font-medium">Webinar: Cum protejăm copilul în conflictele parentale</div>
                  <div className="text-[10px] text-[#6C7A9C] mt-1">19:00 – 21:00</div>
                  <button className="mt-2 text-[10px] font-bold tracking-widest text-[#D4A63D] hover:text-[#F2C053]">ÎNSCRIE-TE →</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 ACTION CTA CARDS */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <ActionCTA icon={LifeBuoy} title="Centrul de Ajutor" text="Ghiduri, informații și resurse pentru situația ta concretă." button="CITEȘTE MAI MULT" to="/ajutor" />
          <ActionCTA icon={Edit3} title="Spune Povestea Ta" text="Experiența ta poate ajuta alți părinți și copii. Nu ești singur." button="ADAUGĂ POVESTEA TA" to="/spune-povestea" variant="outline" />
          <ActionCTA icon={ShieldAlert} title="Semnalează Abuzuri" text="Semnalează anonim practici abuzive sau neetice." button="TRIMITE O SESIZARE" to="/semnaleaza" variant="outline" />
          <ActionCTA icon={Heart} title="Susține această Misiune" text="Fiecare contribuție ne ajută să continuăm lupta pentru copii." button="DONEAZĂ ACUM" to="/doneaza" />
        </div>
      </section>

      {/* 13 TACTICI BANNER (educational hook) */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <div className="relative dense-card overflow-hidden p-6 md:p-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#E89BB8] mb-3">Resursă completă</div>
            <h3 className="font-display text-3xl md:text-4xl text-[#F8F8F4] leading-tight">
              <span className="rose-grad-text">13 tactici</span> de alienare parentală
            </h3>
            <p className="mt-3 text-[14px] text-[#A0AABF] leading-relaxed max-w-md">
              Copilul nu e o armă. Nu este un trofeu. Este o ființă care suferă. Recunoaște tacticile subtile care distrug, încet, legătura părinte-copil.
            </p>
            <div className="mt-5 flex gap-3 flex-wrap">
              <Link to="/articole?category=alienare" className="btn-gold rounded-sm px-5 py-3 text-[11px] font-bold tracking-wider inline-flex items-center gap-2">CITEȘTE GHIDUL <ArrowRight className="w-3.5 h-3.5" /></Link>
              <Link to="/test" className="btn-gold-outline rounded-sm px-5 py-3 text-[11px] font-bold tracking-wider">FĂ TESTUL RAPID</Link>
            </div>
          </div>
          <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-white/10">
            <img src={BRAND.info13Tactici} alt="13 tactici alienare parentală" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* CICLUL ALIENĂRII (educational #2) */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <div className="relative dense-card overflow-hidden p-6 md:p-10 grid md:grid-cols-2 gap-6 items-center">
          <div className="md:order-2">
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#D4A63D] mb-3">Anatomie a abuzului</div>
            <h3 className="font-display text-3xl md:text-4xl text-[#F8F8F4] leading-tight">
              Ciclul alienării parentale
            </h3>
            <p className="mt-3 text-[14px] text-[#A0AABF] leading-relaxed max-w-md">
              Cum un părinte manipulează, controlează și rănește copilul — și pe celălalt părinte — pentru a câștiga puterea. <span className="text-[#F8F8F4]">Nu este iubire. Este frică. Este izolare.</span>
            </p>
            <div className="mt-5 flex gap-3 flex-wrap">
              <Link to="/articole?category=alienare" className="btn-gold-outline rounded-sm px-5 py-3 text-[11px] font-bold tracking-wider inline-flex items-center gap-2">VEZI ANALIZA COMPLETĂ <ArrowRight className="w-3.5 h-3.5" /></Link>
              <Link to="/ajutor" className="btn-gold rounded-sm px-5 py-3 text-[11px] font-bold tracking-wider">CER AJUTOR</Link>
            </div>
          </div>
          <div className="md:order-1 relative aspect-square md:aspect-[4/3] rounded-lg overflow-hidden border border-white/10">
            <img src={BRAND.infoCiclulAlienarii} alt="Ciclul alienării parentale — 11 mecanisme" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* VIZIUNE + LEGI ADOPTATE (2-column vision band) */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="grid md:grid-cols-2 gap-5">
          <Link to="/articole?category=alienare" className="dense-card relative overflow-hidden group block" data-testid="vision-future">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={BRAND.imgFuture} alt="O Românie unde copiii cresc cu ambii părinți" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1020] via-[#0A1020]/30 to-transparent pointer-events-none" />
            </div>
            <div className="p-5 absolute bottom-0 left-0 right-0">
              <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#E89BB8] mb-2">Viziunea noastră</div>
              <h3 className="font-display text-xl md:text-2xl text-[#F8F8F4] leading-tight">O Românie în care familia<br />nu este formulă, ci echipă.</h3>
            </div>
          </Link>
          <Link to="/legislatie" className="dense-card relative overflow-hidden group block" data-testid="vision-law">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={BRAND.imgLawAdopted} alt="Lege adoptată — Dreptul copiilor la ambii părinți" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1020] via-[#0A1020]/30 to-transparent pointer-events-none" />
            </div>
            <div className="p-5 absolute bottom-0 left-0 right-0">
              <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#D4A63D] mb-2">Activism civic</div>
              <h3 className="font-display text-xl md:text-2xl text-[#F8F8F4] leading-tight">Dreptul copiilor la ambii părinți.<br />Lege propusă în Parlament.</h3>
            </div>
          </Link>
        </div>
      </section>

      {/* NEWSLETTER + SUPPORT BAR */}
      <section className="bg-[#080D1A] border-y border-white/5">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-3 gap-6 items-center">
          <div>
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#D4A63D] mb-1.5">Newsletter</div>
            <div className="font-display text-xl text-[#F8F8F4]">Abonează-te la newsletter</div>
            <p className="text-[12px] text-[#A0AABF] mt-1">Primește noutăți, articole și resurse utile.</p>
          </div>
          <form className="md:col-span-2 flex gap-2 flex-col sm:flex-row" onSubmit={(e) => { e.preventDefault(); }}>
            <input type="email" placeholder="Adresa ta de email" data-testid="newsletter-email" className="flex-1 bg-[#0A1020] border border-white/10 rounded-sm px-4 py-3 text-sm text-[#F8F8F4] placeholder-white/30 focus:border-[#D4A63D] outline-none" />
            <button type="submit" data-testid="newsletter-submit" className="btn-gold rounded-sm px-7 py-3 text-[11px] font-bold tracking-wider whitespace-nowrap">MĂ ABONEZ</button>
          </form>
        </div>
      </section>

      {/* SUPPORT MISSION BIG CTA */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="dense-card p-8 md:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ background: "radial-gradient(circle at center, #D4A63D 0%, transparent 60%)" }} />
          <div className="relative">
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#E89BB8] mb-3">O Românie nouă</div>
            <h2 className="font-display text-3xl md:text-5xl text-[#F8F8F4] leading-tight max-w-3xl mx-auto">
              O Românie în care <span className="gold-text">copiii au acces la ambii părinți.</span>
              <span className="block mt-2">O Românie în care <span className="rose-grad-text">iubirea</span> nu este considerată un pericol.</span>
            </h2>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/doneaza" data-testid="bottom-cta-donate" className="btn-gold rounded-sm px-8 py-4 text-[12px] font-bold tracking-wider inline-flex items-center gap-2">
                <Heart className="w-4 h-4" /> SUSȚINE ACEASTĂ MISIUNE
              </Link>
              <Link to="/programare" data-testid="bottom-cta-booking" className="btn-gold-outline rounded-sm px-8 py-4 text-[12px] font-bold tracking-wider inline-flex items-center gap-2">
                <Headphones className="w-4 h-4" /> VORBEȘTE CU GABRIEL
              </Link>
              <Link to="/autentificare" className="btn-ghost-light rounded-sm px-8 py-4 text-[12px] font-bold tracking-wider">DEVINO MEMBRU</Link>
            </div>
            <div className="mt-6 flex items-center justify-center gap-4 text-[#A0AABF]">
              <a href={waLink()} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="hover:text-[#25D366]"><MessageCircle className="w-5 h-5" /></a>
              <a href="#" aria-label="Facebook" className="hover:text-[#1877F2]"><Facebook className="w-5 h-5" /></a>
              <a href="#" aria-label="YouTube" className="hover:text-[#FF0000]"><Youtube className="w-5 h-5" /></a>
              <a href="#" aria-label="Instagram" className="hover:text-[#E4405F]"><Instagram className="w-5 h-5" /></a>
              <a href="#" aria-label="TikTok" className="hover:text-[#E89BB8]"><Music2 className="w-5 h-5" /></a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
